/**
 * StripeSignupCheckout — embedded $0 SetupIntent card validation.
 *
 * Mounts Stripe's <EmbeddedCheckout> using the publishable_key + client_secret
 * returned by /api/revqdemo/signup/intent. When Stripe fires onComplete (card
 * validated, $0 charge confirmed), this component polls /signup/finalize/{id}
 * until the vendor reports status=succeeded — at which point the tenant is
 * created and a JWT is returned.
 *
 * Props:
 *   publishableKey  : string                  pk_live_... | pk_test_...
 *   clientSecret    : string                  Stripe Checkout Session client_secret
 *   sessionId       : string                  Vendor signup session id
 *   onSuccess({ access_token, raw })          Tenant created + JWT issued
 *   onError(err)                              Stripe or polling failure
 *   onCancel()                                User abandoned the card form
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Loader2, X } from 'lucide-react';
import { signupFinalize } from '../../lib/api/ediApi';

const POLL_INTERVAL_MS = 1500;
const POLL_MAX = 30; // ~45s ceiling

const StripeSignupCheckout = ({
  publishableKey, clientSecret, sessionId,
  onSuccess, onError, onCancel,
}) => {
  // Memoize the loadStripe promise — re-creating it on every render breaks
  // the Stripe iframe.
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : Promise.resolve(null)),
    [publishableKey]
  );

  const [polling, setPolling] = useState(false);
  const [pollError, setPollError] = useState('');
  const pollAttempts = useRef(0);

  const startFinalize = async () => {
    setPolling(true);
    pollAttempts.current = 0;
    setPollError('');

    const tick = async () => {
      pollAttempts.current += 1;
      const { ok, status, data } = await signupFinalize(sessionId);
      const vendorStatus = data?.status;

      if (ok && (vendorStatus === 'succeeded' || data?.access_token)) {
        setPolling(false);
        onSuccess?.({ access_token: data.access_token, raw: data });
        return;
      }
      if (data?.status === 'cancelled' || data?.status === 'expired' || status === 410) {
        setPolling(false);
        setPollError(`Signup ${data?.status || 'abandoned'}.`);
        onCancel?.();
        return;
      }
      if (pollAttempts.current >= POLL_MAX) {
        setPolling(false);
        const msg = 'Finalize timeout — Stripe may still be processing. Retry sign-in shortly.';
        setPollError(msg);
        onError?.(new Error(msg));
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
  };

  // Stripe options for embedded checkout — onComplete fires after the card
  // is captured. We then drive the vendor's finalize polling.
  const options = useMemo(() => ({
    clientSecret,
    onComplete: () => { startFinalize(); },
  }), [clientSecret]);

  useEffect(() => () => { pollAttempts.current = POLL_MAX; }, []);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
      data-testid="stripe-checkout-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-900">Card validation ($0)</p>
          <button
            onClick={() => { if (!polling) onCancel?.(); }}
            disabled={polling}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30"
            data-testid="stripe-checkout-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {publishableKey && clientSecret ? (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="p-8 text-center text-sm text-red-600" data-testid="stripe-config-error">
              Stripe not configured — missing publishable key or client secret.
            </div>
          )}
        </div>

        {polling && (
          <div
            className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2 text-xs text-slate-600"
            data-testid="stripe-checkout-finalizing"
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            Card accepted — creating your tenant…
          </div>
        )}
        {pollError && (
          <div
            className="px-5 py-3 border-t border-red-200 bg-red-50 text-xs text-red-700"
            data-testid="stripe-checkout-error"
          >
            {pollError}
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeSignupCheckout;
